import React from "react";

// Controlled, multipart Clinical Validation Plan form for an NGS bioinformatics pipeline.
// Props: { value (plan object), onChange(next), readOnly }. Used both to edit the Resources
// library template defaults and to fill a per-proposal instance. Accuracy metrics
// (sensitivity/specificity/PPV/NPV/accuracy + 95% Wilson CI) are computed live for display.

const lbl = { display: "block", fontSize: 12, fontWeight: 600, color: "#475569", margin: "8px 0 4px" };
const input = { width: "100%", padding: "8px", border: "1px solid #cbd5e1", borderRadius: 6, boxSizing: "border-box" };
const th = { textAlign: "left", padding: "6px 8px", background: "#273d3b", color: "#fff", fontSize: 12 };
const td = { padding: "4px 6px", borderBottom: "1px solid #e2e8f0", verticalAlign: "top" };
const sec = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 16, marginBottom: 16 };
const h = { color: "#213a22", margin: "0 0 4px", fontSize: 16 };
const addBtn = { padding: "6px 12px", border: "none", borderRadius: 6, cursor: "pointer", background: "#dbeafe", color: "#1e3a8a", fontWeight: 600, fontSize: 13 };
const delBtn = { padding: "4px 8px", border: "none", borderRadius: 6, cursor: "pointer", background: "#fee2e2", color: "#991b1b", fontWeight: 600 };
const pct = (x) => (x == null || !isFinite(x) ? "—" : (x * 100).toFixed(2) + "%");

function wilson(k, n) {
    if (!n) return null;
    const z = 1.96, p = k / n, d = 1 + (z * z) / n;
    const c = (p + (z * z) / (2 * n)) / d;
    const m = (z * Math.sqrt((p * (1 - p)) / n + (z * z) / (4 * n * n))) / d;
    return [Math.max(0, c - m), Math.min(1, c + m)];
}
function ciStr(k, n) { const c = wilson(k, n); return c ? `[${pct(c[0])}, ${pct(c[1])}]` : "—"; }

const VARIANT_OPTS = ["SNV", "Indel", "CNV", "SV", "Fusion", "MNV", "STR"];

export default function ClinicalValidationPlanForm({ value, onChange, readOnly }) {
    const v = value || {};
    const g = (section) => v[section] || {};
    const arr = (key) => Array.isArray(v[key]) ? v[key] : [];

    // --- setters (immutable) ---
    const upObj = (section, key, val) => onChange({ ...v, [section]: { ...g(section), [key]: val } });
    const upArr = (key, rows) => onChange({ ...v, [key]: rows });
    const upObjArr = (section, key, rows) => onChange({ ...v, [section]: { ...g(section), [key]: rows } });
    const editRow = (rows, i, key, val) => rows.map((r, j) => (j === i ? { ...r, [key]: val } : r));

    const ro = !!readOnly;
    const F = ({ section, k, label, textarea, type }) => (
        <div>
            <label style={lbl}>{label}</label>
            {textarea
                ? <textarea disabled={ro} style={{ ...input, minHeight: 56 }} value={g(section)[k] || ""} onChange={(e) => upObj(section, k, e.target.value)} />
                : <input disabled={ro} type={type || "text"} style={input} value={g(section)[k] || ""} onChange={(e) => upObj(section, k, e.target.value)} />}
        </div>
    );
    const grid2 = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 20px" };

    // Generic editable table for arrays of flat objects.
    const Table = ({ arrKey, section, columns, blank }) => {
        const rows = section ? (g(section)[arrKey] || []) : arr(arrKey);
        const set = (next) => section ? upObjArr(section, arrKey, next) : upArr(arrKey, next);
        return (
            <>
                <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 6 }}>
                    <thead><tr>{columns.map((c) => <th key={c.key} style={{ ...th, width: c.width }}>{c.label}</th>)}{!ro && <th style={th}></th>}</tr></thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={i}>
                                {columns.map((c) => (
                                    <td key={c.key} style={td}>
                                        {c.textarea
                                            ? <textarea disabled={ro} style={{ ...input, minHeight: 40 }} value={r[c.key] || ""} onChange={(e) => set(editRow(rows, i, c.key, e.target.value))} />
                                            : <input disabled={ro} style={input} value={r[c.key] || ""} onChange={(e) => set(editRow(rows, i, c.key, e.target.value))} />}
                                    </td>
                                ))}
                                {!ro && <td style={td}><button style={delBtn} onClick={() => set(rows.filter((_, j) => j !== i))}>✕</button></td>}
                            </tr>
                        ))}
                        {!rows.length && <tr><td style={td} colSpan={columns.length + 1}>No rows.</td></tr>}
                    </tbody>
                </table>
                {!ro && <button style={{ ...addBtn, marginTop: 8 }} onClick={() => set([...rows, { ...blank }])}>+ Add row</button>}
            </>
        );
    };

    const meta = g("meta"), study = g("study");
    const variantSet = new Set(study.variant_types || []);

    return (
        <div>
            {/* 1. Document control */}
            <div style={sec}>
                <h3 style={h}>1 · Document Control</h3>
                <div style={grid2}>
                    <F section="meta" k="document_title" label="Document title" />
                    <F section="meta" k="document_number" label="Document number" />
                    <F section="meta" k="version" label="Version" />
                    <F section="meta" k="status" label="Status" />
                    <F section="meta" k="effective_date" label="Effective date" type="date" />
                    <F section="meta" k="approval_date" label="Approval date" type="date" />
                    <F section="meta" k="prepared_by" label="Prepared by" />
                    <F section="meta" k="reviewed_by" label="Reviewed by" />
                    <F section="meta" k="approved_by" label="Approved by" />
                </div>
                <F section="meta" k="related_sops" label="Related SOPs / references" textarea />
            </div>

            {/* 2. Study details */}
            <div style={sec}>
                <h3 style={h}>2 · Study Details &amp; Purpose</h3>
                <F section="study" k="purpose" label="Purpose of study" textarea />
                <F section="study" k="intended_use" label="Intended use" textarea />
                <F section="study" k="clinical_indication" label="Clinical indication" textarea />
                <div style={grid2}>
                    <F section="study" k="client_name" label="Client" />
                    <F section="study" k="species" label="Species" />
                    <F section="study" k="sample_types" label="Sample type(s)" />
                    <F section="study" k="matrix" label="Matrix" />
                    <F section="study" k="technology_platform" label="Technology / platform" />
                    <F section="study" k="instrument" label="Instrument" />
                    <F section="study" k="chemistry" label="Chemistry / kit" />
                    <F section="study" k="assay_type" label="Assay type (WGS/WES/panel/RNA-seq)" />
                    <F section="study" k="panel_name" label="Panel name" />
                    <F section="study" k="target_regions" label="Target regions" />
                    <F section="study" k="reference_genome" label="Reference genome" />
                    <F section="study" k="pipeline_name" label="Pipeline name" />
                    <F section="study" k="pipeline_version" label="Pipeline version" />
                </div>
                <label style={lbl}>Variant types under validation</label>
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                    {VARIANT_OPTS.map((vt) => (
                        <label key={vt} style={{ fontSize: 13 }}>
                            <input type="checkbox" disabled={ro} checked={variantSet.has(vt)}
                                onChange={(e) => {
                                    const next = new Set(variantSet);
                                    e.target.checked ? next.add(vt) : next.delete(vt);
                                    upObj("study", "variant_types", Array.from(next));
                                }} /> {vt}
                        </label>
                    ))}
                </div>
                <F section="study" k="endpoints" label="Endpoints" textarea />
                <F section="study" k="regulatory_context" label="Regulatory context" textarea />
            </div>

            {/* 3. Scope */}
            <div style={sec}>
                <h3 style={h}>3 · Scope &amp; Intended Use</h3>
                <div style={grid2}>
                    <F section="scope" k="in_scope" label="In scope" textarea />
                    <F section="scope" k="out_of_scope" label="Out of scope" textarea />
                    <F section="scope" k="target_population" label="Target population" textarea />
                    <F section="scope" k="limitations" label="Limitations" textarea />
                </div>
            </div>

            {/* 4. Reference materials */}
            <div style={sec}>
                <h3 style={h}>4 · Reference Materials &amp; Truth Sets</h3>
                <Table arrKey="reference_materials" blank={{ name: "", source: "", type: "", description: "", provenance: "" }}
                    columns={[{ key: "name", label: "Name" }, { key: "source", label: "Source" }, { key: "type", label: "Type" }, { key: "description", label: "Description", textarea: true }, { key: "provenance", label: "Provenance", textarea: true }]} />
            </div>

            {/* 5. Design */}
            <div style={sec}>
                <h3 style={h}>5 · Validation Design &amp; Methods</h3>
                <F section="design" k="overview" label="Design overview" textarea />
                <F section="design" k="sample_cohort_description" label="Sample cohort" textarea />
                <div style={grid2}>
                    <F section="design" k="num_samples" label="Number of samples" />
                    <F section="design" k="num_replicates" label="Replicates" />
                    <F section="design" k="comparator_method" label="Comparator / orthogonal method" />
                    <F section="design" k="acceptance_overview" label="Acceptance criteria overview" />
                </div>
                <F section="design" k="statistical_methods" label="Statistical methods" textarea />
            </div>

            {/* 6. Pipeline steps */}
            <div style={sec}>
                <h3 style={h}>6 · Bioinformatics Pipeline</h3>
                <Table arrKey="pipeline_steps" blank={{ step: "", tool: "", version: "", parameters: "", purpose: "" }}
                    columns={[{ key: "step", label: "Step" }, { key: "tool", label: "Tool" }, { key: "version", label: "Version", width: 90 }, { key: "parameters", label: "Parameters", textarea: true }, { key: "purpose", label: "Purpose", textarea: true }]} />
            </div>

            {/* 7. QC thresholds */}
            <div style={sec}>
                <h3 style={h}>7 · Quality / Coverage Thresholds</h3>
                <Table arrKey="quality_metrics" blank={{ metric: "", threshold: "", rationale: "" }}
                    columns={[{ key: "metric", label: "Metric" }, { key: "threshold", label: "Threshold", width: 140 }, { key: "rationale", label: "Rationale", textarea: true }]} />
            </div>

            {/* 8. LOD */}
            <div style={sec}>
                <h3 style={h}>8 · Analytical Sensitivity — Limit of Detection (LOD)</h3>
                <F section="lod" k="design" label="LOD study design" textarea />
                <Table section="lod" arrKey="table" blank={{ variant_type: "", target_vaf: "", replicates: "", detected: "", detection_rate: "" }}
                    columns={[{ key: "variant_type", label: "Variant type", width: 120 }, { key: "target_vaf", label: "Target VAF", width: 100 }, { key: "replicates", label: "Replicates", width: 90 }, { key: "detected", label: "Detected", width: 90 }, { key: "detection_rate", label: "Detection rate", width: 120 }]} />
                <F section="lod" k="claim" label="LOD claim" textarea />
            </div>

            {/* 9. Accuracy */}
            <div style={sec}>
                <h3 style={h}>9 · Accuracy — Sensitivity / Specificity / PPV / NPV</h3>
                <p style={{ fontSize: 12, color: "#64748b", margin: "0 0 6px" }}>Enter the confusion matrix per variant type; metrics and 95% Wilson CIs are computed automatically.</p>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead><tr>
                        {["Variant type", "TP", "FP", "TN", "FN", "Sensitivity", "Specificity", "PPV", "NPV", "Accuracy"].map((x) => <th key={x} style={th}>{x}</th>)}
                        {!ro && <th style={th}></th>}
                    </tr></thead>
                    <tbody>
                        {(g("accuracy").by_variant || []).map((r, i) => {
                            const tp = Number(r.tp) || 0, fp = Number(r.fp) || 0, tn = Number(r.tn) || 0, fn = Number(r.fn) || 0;
                            const sens = (tp + fn) ? tp / (tp + fn) : null, spec = (tn + fp) ? tn / (tn + fp) : null;
                            const ppv = (tp + fp) ? tp / (tp + fp) : null, npv = (tn + fn) ? tn / (tn + fn) : null;
                            const acc = (tp + fp + tn + fn) ? (tp + tn) / (tp + fp + tn + fn) : null;
                            const rows = g("accuracy").by_variant || [];
                            const setCell = (k, val) => upObjArr("accuracy", "by_variant", editRow(rows, i, k, val));
                            return (
                                <tr key={i}>
                                    <td style={td}><input disabled={ro} style={input} value={r.variant_type || ""} onChange={(e) => setCell("variant_type", e.target.value)} /></td>
                                    {["tp", "fp", "tn", "fn"].map((k) => (
                                        <td key={k} style={td}><input disabled={ro} type="number" style={{ ...input, width: 70 }} value={r[k] || ""} onChange={(e) => setCell(k, e.target.value)} /></td>
                                    ))}
                                    <td style={td}>{pct(sens)}<div style={{ fontSize: 10, color: "#94a3b8" }}>{ciStr(tp, tp + fn)}</div></td>
                                    <td style={td}>{pct(spec)}<div style={{ fontSize: 10, color: "#94a3b8" }}>{ciStr(tn, tn + fp)}</div></td>
                                    <td style={td}>{pct(ppv)}<div style={{ fontSize: 10, color: "#94a3b8" }}>{ciStr(tp, tp + fp)}</div></td>
                                    <td style={td}>{pct(npv)}</td>
                                    <td style={td}>{pct(acc)}</td>
                                    {!ro && <td style={td}><button style={delBtn} onClick={() => upObjArr("accuracy", "by_variant", rows.filter((_, j) => j !== i))}>✕</button></td>}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {!ro && <button style={{ ...addBtn, marginTop: 8 }} onClick={() => upObjArr("accuracy", "by_variant", [...(g("accuracy").by_variant || []), { variant_type: "", tp: "", fp: "", tn: "", fn: "" }])}>+ Add variant type</button>}
                <F section="accuracy" k="notes" label="Notes" textarea />
            </div>

            {/* 10. Precision */}
            <div style={sec}>
                <h3 style={h}>10 · Precision — Repeatability &amp; Reproducibility</h3>
                <label style={lbl}>Repeatability (intra-run)</label>
                <Table section="precision" arrKey="repeatability" blank={{ condition: "", metric: "", n: "", concordance: "" }}
                    columns={[{ key: "condition", label: "Condition" }, { key: "metric", label: "Metric" }, { key: "n", label: "n", width: 70 }, { key: "concordance", label: "Concordance", width: 120 }]} />
                <label style={lbl}>Reproducibility (inter-run / operator / instrument)</label>
                <Table section="precision" arrKey="reproducibility" blank={{ condition: "", metric: "", n: "", concordance: "" }}
                    columns={[{ key: "condition", label: "Condition" }, { key: "metric", label: "Metric" }, { key: "n", label: "n", width: 70 }, { key: "concordance", label: "Concordance", width: 120 }]} />
                <F section="precision" k="notes" label="Notes" textarea />
            </div>

            {/* 11-14 */}
            <div style={sec}>
                <h3 style={h}>11 · Reportable Range / AMR</h3>
                <div style={grid2}>
                    <F section="reportable_range" k="description" label="Description" textarea />
                    <F section="reportable_range" k="min_depth" label="Minimum depth" />
                    <F section="reportable_range" k="regions_covered" label="Regions covered" />
                    <F section="reportable_range" k="amr" label="Analytical measurement range" />
                </div>
            </div>
            <div style={sec}>
                <h3 style={h}>12 · Reference Range / Interpretation</h3>
                <F section="reference_range" k="description" label="Description" textarea />
            </div>
            <div style={sec}>
                <h3 style={h}>13 · Specimen Requirements &amp; Interfering Substances</h3>
                <F section="specimen" k="requirements" label="Specimen requirements" textarea />
                <F section="specimen" k="interfering_substances" label="Interfering substances" textarea />
            </div>
            <div style={sec}>
                <h3 style={h}>14 · Data Analysis &amp; Statistical Methods</h3>
                <F section="data_analysis" k="statistical_methods" label="Statistical methods" textarea />
                <F section="data_analysis" k="software" label="Software / versions" textarea />
            </div>

            {/* 15. Acceptance criteria */}
            <div style={sec}>
                <h3 style={h}>15 · Acceptance Criteria</h3>
                <Table arrKey="acceptance_criteria" blank={{ parameter: "", criterion: "", observed: "", pass: "" }}
                    columns={[{ key: "parameter", label: "Parameter" }, { key: "criterion", label: "Acceptance criterion", width: 180 }, { key: "observed", label: "Observed", width: 140 }, { key: "pass", label: "Pass/Fail", width: 100 }]} />
            </div>

            {/* 16-19 narrative */}
            <div style={sec}>
                <h3 style={h}>16–19 · Results, Deviations, Limitations &amp; Conclusion</h3>
                <F section="results_summary" k="text" label="Results summary" textarea />
                <F section="deviations" k="text" label="Deviations" textarea />
                <F section="limitations" k="text" label="Limitations" textarea />
                <F section="conclusion" k="text" label="Conclusion" textarea />
            </div>

            {/* 21. References */}
            <div style={sec}>
                <h3 style={h}>20 · References</h3>
                <Table arrKey="references" blank={{ citation: "" }} columns={[{ key: "citation", label: "Citation", textarea: true }]} />
            </div>

            {/* Appendices */}
            <div style={sec}>
                <h3 style={h}>21 · Appendices</h3>
                <Table arrKey="appendices" blank={{ title: "", content: "" }}
                    columns={[{ key: "title", label: "Title", width: 220 }, { key: "content", label: "Content", textarea: true }]} />
            </div>
        </div>
    );
}
